import { ClipboardList, ExternalLink } from 'lucide-react';

import { t } from '@/i18n';
import { EmptyState } from '@/shared';

import type { FinancialCalendarEvent } from '@/entities/financial-calendar';
import {
  getEventCategoryBadgeClass,
  eventTimeLabel,
  formatEventDateHeading,
  getEventCategoryLabel,
  getCountryFlag,
  getEventTitle,
  getValueTrend,
} from '../model/utils';
import { ImportanceBadge } from './ImportanceBadge';
import { ReleaseBadge } from './ReleaseBadge';
import { formatCalendarValue } from '../model/actual-display';

interface EventDetailPanelProps {
  event: FinancialCalendarEvent | null;
}

/**
 * 방향은 **기호로만** 나타내고 색은 중립으로 둔다 (TRAD-2122 / SoT-17).
 * 지표마다 방향↔의미(호재/악재) 매핑이 달라 단일 색 규칙이 성립하지 않는다 — 자세한 근거는
 * `model/utils.ts` 의 SoT-17 주석 참조. 색을 다시 붙이지 말 것.
 */
const TrendArrow = ({ actual, previous }: { actual: string | null; previous: string | null }) => {
  const trend = getValueTrend(actual, previous);
  if (!trend || trend === 'neutral') return null;
  return <span className="text-muted-foreground">{trend === 'up' ? ' ▲' : ' ▼'}</span>;
};

export const EventDetailPanel = ({ event }: EventDetailPanelProps) => {
  // TRAD-2106 — 공용 `shared/ui/EmptyState` 로 이관. `📋` 이모지 아이콘 → lucide `ClipboardList`
  // (감사 SoT-14), 하드코딩 한글 → 카탈로그 키.
  // ⚠️ 이 자리는 "결과 0건"이 아니라 **"아직 선택 안 함"** 이다. 규격(role=status · 재시도 없음)은
  // 같지만 사실이 다르므로 빈 목록 문구를 재사용하지 않고 전용 키를 쓴다.
  if (!event) {
    return (
      <EmptyState
        size="md"
        icon={ClipboardList}
        titleKey="financial_calendar.detail.select_prompt"
        className="h-full"
      />
    );
  }

  // TRAD-2897 — 목록 카드와 같은 함수(« KST» 는 실제 시각에만).
  const time = eventTimeLabel(event, { withZone: true });
  // TRAD-2917 — 목록 헤더와 같은 날짜 표기(Intl 경유)로 통일. `KST`/"종일" 시각 표기는
  // formatEventDateHeading이 손대지 않는 별도 영역이라 아래 렌더에서 그대로 이어붙인다.
  const date = formatEventDateHeading(event.scheduledAt.slice(0, 10));
  // TRAD-2918 — forecast는 정책상(2026-07-30 davidyoon 승인) 무료 API로 못 채워 항상
  // null이다(backend 실측: INDICATOR 33건 전수 forecast 있는 건 0건). hasValues 판정에서도 뺀다.
  const hasValues = event.actual !== null || event.previous !== null;
  const trend = getValueTrend(event.actual, event.previous);

  return (
    <div className="p-4 space-y-4 overflow-y-auto h-full">
      {/* Badge row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${getEventCategoryBadgeClass(event.category)}`}>
          {getEventCategoryLabel(event.category)}
        </span>
        <span className="text-sm">{getCountryFlag(event.country)}</span>
        <ImportanceBadge impact={event.impact} />
        {/* TRAD-2899 — 상세에도 발표 상태 배지(iOS 와 맞춤). */}
        <ReleaseBadge event={event} />
      </div>

      {/* Title */}
      <h2 className="font-semibold text-base leading-snug">{getEventTitle(event)}</h2>

      {/* Date / time */}
      {/* TRAD-2751 — "종일"은 EventDetailDrawer(모바일 쌍)와 완전히 같은 자리·같은 문구라
          신규 키를 만들지 않고 기존 financial_calendar.event.all_day(카탈로그엔 이미 있었으나
          소비처 0건이던 키)를 재사용 — 두 경로가 같은 키를 쓰므로 값이 갈릴 수 없다. */}
      <p className="text-sm text-muted-foreground">
        {date} {time}
      </p>

      {/* Values */}
      {/* TRAD-2918 — forecast 칸 제거(davidyoon 정책 확정 "actual/previous 2칸만", designer
          (가) 채택). 응답 필드(event.forecast)는 안 지운다 — 유료 API 전환 시 되돌릴 수 있게
          UI만 뺀다. `financial_calendar.values.forecast` 카탈로그 키는 소비처 0이 되지만
          삭제는 TRAD-2930(android 죽은 키와 같은 커밋)에서 처리 — 이 커밋에서 안 건드린다. */}
      {hasValues && (
        <div className="rounded-lg border border-border p-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">{t('financial_calendar.values.section')}</p>
          <div className="grid grid-cols-2 gap-2 text-sm text-center">
            <div>
              {/* TRAD-2909/2917 — 옛 `financial_calendar.values.actual`("실제값")에서 카드와
                  공유하는 `financial_calendar.card.actual`("실제")로 전환. 목록 카드와 상세가
                  같은 개념을 다른 낱말로 부르던 걸 병합(planner 확정, TRAD-2909 코멘트 §B). */}
              <p className="text-xs text-muted-foreground mb-0.5">{t('financial_calendar.card.actual')}</p>
              <p className="font-semibold">
                {formatCalendarValue(event, 'actual') ?? '—'}
                <TrendArrow actual={event.actual} previous={event.previous} />
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">{t('financial_calendar.card.previous')}</p>
              <p>{formatCalendarValue(event, 'previous') ?? '—'}</p>
            </div>
          </div>
          {/* 비교 기준을 화면에 명시한다 — ▲▼ 만 있으면 "무엇 대비"인지 알 수 없다.
              기간(전월/전분기 등)은 API 가 주지 않으므로 단정하지 않는다(아래 보고 참조). */}
          {trend && trend !== 'neutral' && (
            <p className="mt-2 text-xs text-muted-foreground">{t('financial_calendar.values.trend_note')}</p>
          )}
        </div>
      )}

      {/* Description */}
      {/* TRAD-2751 — "설명"/"원문 보기"도 EventDetailDrawer와 같은 자리·같은 문구라 공유 키
          (financial_calendar.detail.description_label / .source_link)로 뺐다. 🔗 이모지는
          TRAD-2106 선례대로 lucide ExternalLink로 교체(카탈로그 문구엔 이모지를 넣지 않는다 —
          SourceReferenceBlock.tsx가 이미 쓰는 같은 아이콘, 신규 시각 언어 아님). */}
      {event.description && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">
            {t('financial_calendar.detail.description_label')}
          </p>
          <p className="text-sm leading-relaxed text-foreground/80">{event.description}</p>
        </div>
      )}

      {/* Source URL */}
      {event.sourceUrl && (
        <a
          href={event.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-brand-text hover:underline"
        >
          <ExternalLink size={14} />
          {t('financial_calendar.detail.source_link')}
        </a>
      )}

      {/* CoinMarketCal attribution */}
      {event.category === 'CRYPTO' && <p className="text-2xs text-muted-foreground">Powered by CoinMarketCal</p>}
    </div>
  );
};
